// Re-join seam-split tops for the 3D view so the counter reads as one piece.
const near = (a, b) => Math.abs(a[0] - b[0]) < 0.05 && Math.abs(a[1] - b[1]) < 0.05;
const r2 = (n) => Math.round(n * 100) / 100;

const world = (p) => (p.points || []).map(([x, y]) => [x + (Number(p.x) || 0), y + (Number(p.y) || 0)]);

const rotateFrom = (arr, start) => arr.map((_, k) => arr[(start + k) % arr.length]);

const dropCollinear = (pts, edges) => {
  const outP = [];
  const outE = [];
  const n = pts.length;
  for (let i = 0; i < n; i++) {
    const a = pts[(i - 1 + n) % n];
    const p = pts[i];
    const b = pts[(i + 1) % n];
    const cross = (p[0] - a[0]) * (b[1] - a[1]) - (p[1] - a[1]) * (b[0] - a[0]);
    if (Math.abs(cross) > 0.01) {
      outP.push(p);
      outE.push(edges[i] || {});
    } else if (outE.length) {
      // vertex removed: the edge leaving it folds into the edge arriving at it
      outE[outE.length - 1] = { ...(outE[outE.length - 1] || {}), ...(edges[i]?.appliance ? { appliance: edges[i].appliance } : {}) };
    }
  }
  return { pts: outP, edges: outE };
};

const joinTwo = (p, q) => {
  if ((Number(p.rot) || 0) !== 0 || (Number(q.rot) || 0) !== 0) return null;
  const P = world(p);
  const Q = world(q);
  for (let i = 0; i < P.length; i++) {
    if (!p.edges?.[i]?.seam) continue;
    const a = P[i];
    const b = P[(i + 1) % P.length];
    for (let j = 0; j < Q.length; j++) {
      if (!q.edges?.[j]?.seam) continue;
      const c = Q[j];
      const d = Q[(j + 1) % Q.length];
      if (!(near(a, d) && near(b, c))) continue;
      const pPts = rotateFrom(P, (i + 1) % P.length); // b … a
      const pEd = rotateFrom(p.edges || [], (i + 1) % P.length);
      const qPts = rotateFrom(Q, (j + 1) % Q.length); // d … c
      const qEd = rotateFrom(q.edges || [], (j + 1) % Q.length);
      const pts = [...pPts, ...qPts.slice(1, -1)];
      const edges = [...pEd.slice(0, -1), ...qEd.slice(0, -1)];
      const clean = dropCollinear(pts, edges);
      const xs = clean.pts.map((v) => v[0]);
      const ys = clean.pts.map((v) => v[1]);
      const x0 = Math.min(...xs);
      const y0 = Math.min(...ys);
      const shift = (src) =>
        (src.cutouts || []).map((c0) => ({
          ...c0,
          x: r2((Number(c0.x) || 0) + (Number(src.x) || 0) - x0),
          y: r2((Number(c0.y) || 0) + (Number(src.y) || 0) - y0),
        }));
      const all = [...shift(p), ...shift(q)];
      // seam halves of one sink become the full cut again
      const cutouts = all
        .filter((c0) => !c0.seam_part)
        .map((c0) => {
          const rest = all.find((o) => o.seam_part && o.part_of === c0.id);
          if (!rest) return c0;
          const cx0 = Math.min(c0.x, rest.x);
          const cy0 = Math.min(c0.y, rest.y);
          const cx1 = Math.max(c0.x + (Number(c0.w) || 0), rest.x + (Number(rest.w) || 0));
          const cy1 = Math.max(c0.y + (Number(c0.h) || 0), rest.y + (Number(rest.h) || 0));
          const { seam_side, ...base } = c0;
          return { ...base, x: cx0, y: cy0, w: r2(cx1 - cx0), h: r2(cy1 - cy0) };
        });
      const halves = [p, q].flatMap((h) => h.merged_from || [{ id: h.id, x: h.x, y: h.y, rot: h.rot || 0, points: h.points, edges: h.edges, cutouts: h.cutouts, length_in: h.length_in, width_in: h.width_in }]);
      return {
        ...p,
        label: (p.label || "").replace(/\s+[AB]$/, ""),
        x: r2(x0),
        y: r2(y0),
        rot: 0,
        points: clean.pts.map(([x, y]) => [r2(x - x0), r2(y - y0)]),
        edges: clean.edges.map((e) => ({ ...e, seam: false })),
        length_in: r2(Math.max(...xs) - x0),
        width_in: r2(Math.max(...ys) - y0),
        cutouts,
        merged_from: halves.map((h) => ({ ...h, dx: r2((Number(h.x) || 0) - x0), dy: r2((Number(h.y) || 0) - y0) })),
      };
    }
  }
  return null;
};

export const mergeSeamPieces = (pieces) => {
  let list = [...pieces];
  let joined = true;
  while (joined) {
    joined = false;
    outer: for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const m = joinTwo(list[i], list[j]);
        if (m) {
          list = [...list.slice(0, i), m, ...list.slice(i + 1, j), ...list.slice(j + 1)];
          joined = true;
          break outer;
        }
      }
    }
  }
  return list;
};

// Expand a moved merged top back into its original halves for writing to the drawing.
export const expandMerged = (arranged) =>
  arranged.flatMap((p) => {
    if (!p.merged_from) return [p];
    const rot = ((Number(p.rot) || 0) * Math.PI) / 180;
    const cx = p.length_in / 2;
    const cy = p.width_in / 2;
    return p.merged_from.map((h) => {
      const hcx = h.dx + (Number(h.length_in) || 0) / 2;
      const hcy = h.dy + (Number(h.width_in) || 0) / 2;
      const rx = cx + (hcx - cx) * Math.cos(rot) - (hcy - cy) * Math.sin(rot);
      const ry = cy + (hcx - cx) * Math.sin(rot) + (hcy - cy) * Math.cos(rot);
      return {
        ...h,
        x: r2(p.x + rx - (Number(h.length_in) || 0) / 2),
        y: r2(p.y + ry - (Number(h.width_in) || 0) / 2),
        rot: Number(p.rot) || 0,
        lift: p.lift,
        attach: p.attach,
        stand: p.stand,
      };
    });
  });
